import React, { useEffect } from 'react';
import {
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Switch,
  Typography,
} from 'antd';
import type { Item } from '@/services/inventory.service';
import { useQuickCreateItem, useUomUnits, useWarehouses } from '@/hooks/useInventory';

const { Text } = Typography;

export type QuickCreateItemModalProps = {
  open: boolean;
  onCancel: () => void;
  onCreated: (item: Item) => void;
  /** Prefill item name (e.g. from ItemSelect search). */
  presetName?: string;
};

/**
 * Lean item create for estimation / BOQ: name + UOM required;
 * optional critical flag; optional opening / initial stock (warehouse + qty).
 * Does not use the Excel Opening Stock go-live batch.
 */
export const QuickCreateItemModal: React.FC<QuickCreateItemModalProps> = ({
  open,
  onCancel,
  onCreated,
  presetName,
}) => {
  const [form] = Form.useForm();
  const recordStock = Form.useWatch('record_stock', form);
  const isCritical = Form.useWatch('is_critical', form);
  const createMutation = useQuickCreateItem();
  const { data: uoms = [], isLoading: uomsLoading } = useUomUnits();
  const { data: warehouses = [], isLoading: whLoading } = useWarehouses();

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    form.setFieldsValue({
      item_name: String(presetName || '').trim().slice(0, 255),
      base_uom: 'PCS',
      record_stock: false,
      is_critical: false,
      standard_cost: undefined,
      item_code: undefined,
      warehouse_id: undefined,
      quantity: 1,
      unit_rate: undefined,
      heat_number: undefined,
      dimension: undefined,
    });
  }, [open, presetName, form]);

  const handleOk = async () => {
    const values = await form.validateFields();
    const critical = Boolean(values.is_critical);
    const payload: {
      item_name: string;
      base_uom: string;
      item_code?: string;
      standard_cost?: number;
      is_critical?: boolean;
      heat_number_mandatory?: boolean;
      mtc_required_on_receipt?: boolean;
      initial_stock?: {
        warehouse_id: string;
        quantity: number;
        unit_rate?: number;
        heat_number?: string;
        dimension?: string;
      };
    } = {
      item_name: String(values.item_name).trim(),
      base_uom: String(values.base_uom).trim(),
      item_code: values.item_code ? String(values.item_code).trim() : undefined,
      standard_cost:
        values.standard_cost != null && values.standard_cost !== ''
          ? Number(values.standard_cost)
          : undefined,
      is_critical: critical,
      // Critical defaults: heat no. and MTC mandatory on receipt (adjust later in Item master).
      heat_number_mandatory: critical,
      mtc_required_on_receipt: critical,
    };
    if (values.record_stock) {
      payload.initial_stock = {
        warehouse_id: String(values.warehouse_id),
        quantity: Number(values.quantity),
        unit_rate:
          values.unit_rate != null && values.unit_rate !== ''
            ? Number(values.unit_rate)
            : payload.standard_cost,
        heat_number: values.heat_number ? String(values.heat_number).trim() : undefined,
        dimension: values.dimension ? String(values.dimension).trim() : undefined,
      };
    }
    const result = await createMutation.mutateAsync(payload);
    onCreated(result.item);
  };

  return (
    <Modal
      title="Create new item"
      open={open}
      onOk={() => void handleOk()}
      onCancel={onCancel}
      confirmLoading={createMutation.isPending}
      destroyOnClose
      okText="Create"
      width={520}
    >
      <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
        Only name and UOM are required. Mark critical if heat/dimension must be tracked on stock.
        Opening stock is optional (not the company Excel opening-stock upload).
      </Text>
      <Form form={form} layout="vertical" requiredMark="optional">
        <Form.Item
          name="item_name"
          label="Item name"
          rules={[{ required: true, message: 'Item name is required' }]}
        >
          <Input placeholder="e.g. ROUND BAR - DIA: 25.4mm" maxLength={255} />
        </Form.Item>
        <Form.Item
          name="base_uom"
          label="Base UOM"
          rules={[{ required: true, message: 'Base UOM is required' }]}
        >
          <Select
            showSearch
            loading={uomsLoading}
            placeholder="Select UOM"
            optionFilterProp="label"
            options={
              uoms.length
                ? uoms.map((u) => ({
                    value: u.code,
                    label: u.code,
                  }))
                : [
                    { value: 'PCS', label: 'PCS' },
                    { value: 'KG', label: 'KG' },
                    { value: 'M', label: 'M' },
                    { value: 'SQM', label: 'SQM' },
                    { value: 'LTR', label: 'LTR' },
                  ]
            }
          />
        </Form.Item>
        <Form.Item name="item_code" label="Item code (optional)">
          <Input placeholder="Leave blank to auto-generate" maxLength={50} />
        </Form.Item>
        <Form.Item name="standard_cost" label="Standard / unit cost (optional)">
          <InputNumber min={0} style={{ width: '100%' }} precision={4} />
        </Form.Item>
        <Form.Item
          name="is_critical"
          label="Critical material"
          valuePropName="checked"
          extra="Critical items require heat number and dimension whenever stock is received or issued."
        >
          <Switch />
        </Form.Item>
        <Form.Item
          name="record_stock"
          label="Record opening / initial stock"
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>
        {recordStock ? (
          <>
            <Form.Item
              name="warehouse_id"
              label="Warehouse"
              rules={[{ required: true, message: 'Warehouse is required' }]}
            >
              <Select
                showSearch
                loading={whLoading}
                placeholder="Select warehouse"
                optionFilterProp="label"
                options={(warehouses || []).map((w) => ({
                  value: w.id,
                  label: `${w.warehouse_code} — ${w.warehouse_name}`,
                }))}
              />
            </Form.Item>
            <Form.Item
              name="quantity"
              label="Quantity"
              rules={[
                { required: true, message: 'Quantity is required' },
                {
                  validator: async (_, v) => {
                    if (v == null || Number(v) <= 0) {
                      throw new Error('Quantity must be greater than zero');
                    }
                  },
                },
              ]}
            >
              <InputNumber min={0.0001} style={{ width: '100%' }} precision={4} />
            </Form.Item>
            <Form.Item name="unit_rate" label="Unit rate (optional)">
              <InputNumber min={0} style={{ width: '100%' }} precision={4} />
            </Form.Item>
            {isCritical ? (
              <>
                <Form.Item
                  name="heat_number"
                  label="Heat number"
                  rules={[{ required: true, message: 'Heat number is required for critical stock' }]}
                >
                  <Input placeholder="Heat / cast number" maxLength={100} />
                </Form.Item>
                <Form.Item
                  name="dimension"
                  label="Dimension"
                  rules={[{ required: true, message: 'Dimension is required for critical stock' }]}
                >
                  <Input placeholder="e.g. 25.4mm" maxLength={100} />
                </Form.Item>
              </>
            ) : null}
          </>
        ) : null}
      </Form>
    </Modal>
  );
};

export default QuickCreateItemModal;
